import React, { useState } from 'react';
import './NavigationMenu.css';
import { SearchPanel } from './SearchPanel';
import { resetLiveFabPosition } from './LiveFab';

interface NavigationMenuProps {
  onNavigate: (section: string) => void;
  activeSection: string | null;
  walletAddress: string | null;
  isAdmin: boolean;
  isAdminOrCountryAdmin?: boolean;
  onSettingsClick?: () => void;
  map: mapboxgl.Map | null;
  markerUpdateTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  pendingDepositsCount?: number;
}

export const NavigationMenu: React.FC<NavigationMenuProps> = ({
  onNavigate,
  activeSection,
  walletAddress,
  isAdmin,
  isAdminOrCountryAdmin = false,
  onSettingsClick,
  map,
  markerUpdateTimeoutRef,
  pendingDepositsCount = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSearchPanel, setShowSearchPanel] = useState(false);

  const menuItems = [
    { id: 'dashboard',        label: 'Dashboard',        icon: '📊', requiresWallet: false },
    { id: 'live-directory',   label: 'Live broadcasts',  icon: '📡', requiresWallet: false },
    { id: 'smart-contracts',  label: 'Smart Contracts',  icon: '📜', requiresWallet: false },
    { id: 'spatial-objects',  label: 'Spatial Objects',  icon: '🏙️', requiresWallet: true  },
    { id: 'sessions',         label: 'Sessions',         icon: '🎮', requiresWallet: false },
    { id: 'trust-groups',     label: 'Trust Groups',     icon: '👥', requiresWallet: true  },
    { id: 'pending-deposits', label: 'Pending Deposits', icon: '💰', requiresWallet: true  },
    { id: 'border-proposals', label: 'Border Proposals', icon: '🗺️', requiresWallet: true, requiresAdminOrCountryAdmin: true },
    { id: 'compliance-lists', label: 'Compliance Lists', icon: '🛡️', requiresWallet: true, requiresAdminOrCountryAdmin: true },
    { id: 'register-test-entity', label: 'Register test entity', icon: '🧪', requiresWallet: true, requiresAdmin: true },
    { id: 'admin',            label: 'Admin',            icon: '⚙️', requiresWallet: true, requiresAdmin: true },
    { id: 'api-docs',         label: 'API Docs',         icon: '📖', requiresWallet: false, isExternalLink: true,
      href: process.env.REACT_APP_API_URL
        ? `${process.env.REACT_APP_API_URL.replace(/\/api$/, '')}/api/docs/`
        : (process.env.NODE_ENV === 'production'
            ? 'https://geotrust.stellargeolink.com/api/docs/'
            : 'http://localhost:8080/api/docs/') },
  ].filter((item: any) => {
    if (item.requiresWallet && !walletAddress) return false;
    if (item.requiresAdmin && !isAdmin) return false;
    if (item.requiresAdminOrCountryAdmin && !isAdminOrCountryAdmin) return false;
    return true;
  });

  const handleItemClick = (itemId: string) => {
    const item = menuItems.find(i => i.id === itemId) as any;
    if (item?.isExternalLink) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
      setIsOpen(false);
      return;
    }
    onNavigate(itemId);
    setIsOpen(false);
  };

  const handleSearchClick = () => {
    setShowSearchPanel(!showSearchPanel);
  };

  const handleSettingsClick = () => {
    if (onSettingsClick) {
      onSettingsClick();
    }
  };

  return (
    <div className="navigation-menu">
      {/* Search and Settings buttons to the left of hamburger */}
      <div className="navigation-menu-actions">
        <button
          className="navigation-action-button"
          onClick={handleSearchClick}
          title="Search"
          aria-label="Search"
        >
          🔍
        </button>
        <button
          className="navigation-action-button"
          onClick={handleSettingsClick}
          title="Settings"
          aria-label="Settings"
        >
          ⚙️
        </button>
      </div>

      <button
        className="navigation-menu-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title="Menu"
        aria-label="Toggle menu"
        style={{ position: 'relative' }}
      >
        <span className="hamburger-icon">
          <span></span>
          <span></span>
          <span></span>
        </span>
        {pendingDepositsCount > 0 && (
          <span className="nav-badge">
            {pendingDepositsCount > 99 ? '99+' : pendingDepositsCount}
          </span>
        )}
      </button>
      
      {isOpen && (
        <nav className="navigation-menu-items">
          {menuItems.map(item => (
            <button
              key={item.id}
              className={`navigation-menu-item ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => handleItemClick(item.id)}
              title={item.label}
            >
              <span className="navigation-menu-icon">{item.icon}</span>
              <span className="navigation-menu-label">{item.label}</span>
              {item.id === 'pending-deposits' && pendingDepositsCount > 0 && (
                <span className="nav-badge nav-badge-inline">
                  {pendingDepositsCount > 99 ? '99+' : pendingDepositsCount}
                </span>
              )}
            </button>
          ))}
          {/*
            Escape hatch for Tesla / kiosk users whose browsers don't
            deliver pointer events to parts of the viewport where
            the FAB may have been dragged or defaulted.  Tapping
            "Reset Live button" teleports it back to a known-good
            centre-bottom position so they can find and re-drag it.
          */}
          <button
            className="navigation-menu-item"
            onClick={() => { resetLiveFabPosition(); setIsOpen(false); }}
            title="Move the floating 📡 Live button back to its default position"
            style={{ borderTop: '1px solid rgba(0,0,0,0.08)', marginTop: 4 }}
          >
            <span className="navigation-menu-icon">📍</span>
            <span className="navigation-menu-label">Reset Live button position</span>
          </button>
        </nav>
      )}

      {/* Search Panel - opens to the left of search button */}
      <SearchPanel
        isOpen={showSearchPanel}
        onClose={() => setShowSearchPanel(false)}
        map={map}
        markerUpdateTimeoutRef={markerUpdateTimeoutRef}
      />
    </div>
  );
};
